import {
  isAgentChatNativeApp,
  isNativeExternalOpenAvailable,
  openNativeExternalUrl,
} from './nativeExternalOpen'
import {
  OUTLOOK_APP_PACKAGE,
  outlookFolderUrl,
  outlookFolderIntentUrl,
  type OutlookFolder,
} from './emailDraftPayload'

const viteEnv = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>
}).env ?? {}
const PRODUCTION_ORIGIN = (viteEnv.VITE_AGENTCHAT_PRODUCTION_ORIGIN ?? '').trim()
const LOCAL_FALLBACK_ORIGIN = 'http://localhost'
const UNSAFE_SCHEMES = new Set(['javascript:', 'data:', 'vbscript:'])

function currentOrigin(): string {
  if (typeof window === 'undefined') return PRODUCTION_ORIGIN || LOCAL_FALLBACK_ORIGIN
  const origin = window.location.origin
  if (
    PRODUCTION_ORIGIN
    && (origin.includes('127.0.0.1') || origin.includes('localhost'))
  ) return PRODUCTION_ORIGIN
  return origin
}

export function absoluteAppUrl(pathOrUrl: string): string {
  try {
    return new URL(pathOrUrl, currentOrigin()).href
  } catch {
    return pathOrUrl
  }
}

function scheme(value: string): string | null {
  const match = value.trim().match(/^([a-z][a-z\d+.-]*):/i)
  return match ? `${match[1].toLowerCase()}:` : null
}

export function isUnsafeExternalHref(pathOrUrl: string | undefined | null): boolean {
  const raw = (pathOrUrl ?? '').trim()
  const rawScheme = scheme(raw)
  if (rawScheme && UNSAFE_SCHEMES.has(rawScheme)) return true
  const resolvedScheme = scheme(absoluteAppUrl(raw))
  return !!resolvedScheme && UNSAFE_SCHEMES.has(resolvedScheme)
}

function isHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function isAndroidRuntime(): boolean {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)
}

export function isStandaloneAppSurface(): boolean {
  return (
    typeof window !== 'undefined'
    && window.matchMedia('(display-mode: standalone)').matches
  )
}

export function androidChromeIntentUrl(url: string): string {
  const target = new URL(url)
  const path = `${target.host}${target.pathname}${target.search}${target.hash}`
  return `intent://${path}#Intent;scheme=${target.protocol.replace(':', '')};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`
}

/**
 * Open an Outlook mail folder ("drafts" before sending, "sent" after),
 * preferring the Outlook *app*. Outlook mobile exposes no per-message edit /
 * open-by-id deep link that works for our drafts (verified by decompiling the
 * app — see outlookFolderUrl), so we land on the folder: the item sits at the
 * top, one tap into Outlook's native editor.
 *  - Native APK → the native plugin fires the ms-outlook:// folder link at the
 *    Outlook package; if Outlook isn't installed, the plugin opens the editable
 *    web draft instead (its built-in ActivityNotFound fallback).
 *  - Standalone Android PWA → Chrome dispatches an intent:// to Outlook with
 *    the web draft as browser_fallback_url.
 *  - Desktop → open the editable web draft directly (no app to target).
 *
 * `webFallbackUrl` is the editable OWA draft URL. A plain markdown
 * `ms-outlook://` link only resets the PWA (the WebView never hands a custom
 * scheme to Android); routing through the native plugin / an intent:// URL is
 * what actually launches the app.
 */
export async function openOutlookFolder(
  folder: OutlookFolder,
  webFallbackUrl: string | undefined | null,
): Promise<void> {
  const fallback = webFallbackUrl || absoluteAppUrl('/')
  if (isAgentChatNativeApp() && isNativeExternalOpenAvailable()) {
    await openNativeExternalUrl({
      url: outlookFolderUrl(folder),
      fallbackUrl: fallback,
      packageName: OUTLOOK_APP_PACKAGE,
      chooserTitle: 'Open in Outlook',
    })
    return
  }
  if (isAndroidRuntime() && isStandaloneAppSurface()) {
    const opened = window.open(
      outlookFolderIntentUrl(folder, fallback),
      '_blank',
      'noopener,noreferrer',
    )
    if (!opened) {
      throw new Error('Open in Outlook was blocked by the browser.')
    }
    return
  }
  const opened = window.open(fallback, '_blank', 'noopener,noreferrer')
  if (!opened) {
    throw new Error('Open in Outlook was blocked by the browser.')
  }
}

export function externalBrowserHref(pathOrUrl: string): string {
  const url = absoluteAppUrl(pathOrUrl)
  if (isAndroidRuntime() && !isAgentChatNativeApp() && isStandaloneAppSurface() && isHttpUrl(url)) {
    return androidChromeIntentUrl(url)
  }
  return url
}

export function shouldInterceptExternalBrowserClick(pathOrUrl: string): boolean {
  const url = absoluteAppUrl(pathOrUrl)
  if (isUnsafeExternalHref(url)) return false
  if (isAgentChatNativeApp()) return true
  return isAndroidRuntime() && isStandaloneAppSurface() && isHttpUrl(url)
}

export function isCommitmentsDashboardHref(href: string | undefined): boolean {
  if (!href) return false
  try {
    return new URL(href, currentOrigin()).pathname === '/commitments'
  } catch {
    return false
  }
}

export async function openExternalBrowser(pathOrUrl: string): Promise<void> {
  const url = absoluteAppUrl(pathOrUrl)
  if (isUnsafeExternalHref(url)) return
  const browserPackage = isHttpUrl(url) ? 'com.android.chrome' : null
  if (isAgentChatNativeApp() && isNativeExternalOpenAvailable()) {
    await openNativeExternalUrl({
      url,
      fallbackUrl: url,
      packageName: browserPackage,
      chooserTitle: browserPackage ? 'Open in browser' : 'Open link',
    })
    return
  }
  if (isAndroidRuntime() && isStandaloneAppSurface() && browserPackage) {
    window.location.assign(androidChromeIntentUrl(url))
    return
  }
  const opened = window.open(url, '_blank', 'noopener,noreferrer')
  if (!opened) window.location.assign(url)
}
